import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Mousewheel, Keyboard } from "swiper/modules";
import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import { getMediaRecordUrl, getMediaUrl } from "@/utils/media";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const isVideoPath = (path, media) =>
  media?.mediaType?.includes("video") ||
  /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(path || "");

const getPosterUrl = (media) =>
  getMediaRecordUrl(media, { variant: "thumbnail" }) ||
  media?.placeholder ||
  media?.placeholderPath ||
  undefined;

const PlacesImagesSlider = ({
  isOpen,
  filePaths,
  onClose,
  placeMedia,
  currentSliderIndex = 0,
}) => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(currentSliderIndex);
  const canNavigate = (filePaths?.length || 0) > 1;

  useEffect(() => {
    if (isOpen && swiperRef.current) {
      swiperRef.current.slideTo(currentSliderIndex, 0);
      setActiveIndex(currentSliderIndex);
    }
  }, [isOpen, currentSliderIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[98] flex h-[100dvh] w-screen select-none flex-col items-center justify-center gap-4 overflow-hidden bg-black/70 px-4 py-8 text-white backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 z-[99] rounded-full"
      >
        <Iconify icon="ion:close" className="!h-8 !min-w-8 !text-white" />
      </button>

      <div className="absolute inset-0 flex items-center justify-center px-4 py-12">
        <div
          className="relative flex h-full w-full max-w-[min(94vw,1100px)] items-center justify-center"
          onClick={(event) => event.stopPropagation()}
        >
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              swiper.slideTo(currentSliderIndex, 0);
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex);
            }}
            pagination={{ dynamicBullets: true }}
            keyboard={{ enabled: true }}
            mousewheel={true}
            spaceBetween={30}
            slidesPerView={1}
            modules={[Pagination, Mousewheel, Keyboard]}
            className="h-full w-full"
          >
            {filePaths?.map((filePath, index) => (
              <SwiperSlide
                key={`file-${index}`}
                onClick={(event) => event.stopPropagation()}
                className="!flex h-full items-center justify-center"
              >
                <div className="flex h-full w-full select-none items-center justify-center">
                  {isVideoPath(filePath, placeMedia?.[index]) ? (
                    <video
                      src={getMediaUrl(filePath)}
                      poster={getPosterUrl(placeMedia?.[index])}
                      className="block max-h-full max-w-full rounded-lg object-contain"
                      muted
                      playsInline
                      controls
                      preload="metadata"
                      controlsList="nodownload noremoteplayback"
                      onContextMenu={(event) => event.preventDefault()}
                    />
                  ) : (
                    <img
                      src={getMediaUrl(filePath)}
                      alt="place-media"
                      className="block max-h-full max-w-full rounded-lg object-contain"
                      referrerPolicy="no-referrer"
                      draggable={false}
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {canNavigate && (
            <>
              <button
                type="button"
                aria-label="Previous media"
                onClick={(event) => {
                  event.stopPropagation();
                  swiperRef.current?.slidePrev();
                }}
                className="absolute left-3 top-1/2 z-[99] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white backdrop-blur transition hover:bg-black/50 md:-left-14"
              >
                <Iconify
                  icon="lucide:chevron-left"
                  className="!h-7 !w-7 !text-inherit"
                />
              </button>
              <button
                type="button"
                aria-label="Next media"
                onClick={(event) => {
                  event.stopPropagation();
                  swiperRef.current?.slideNext();
                }}
                className="absolute right-3 top-1/2 z-[99] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/35 text-white backdrop-blur transition hover:bg-black/50 md:-right-14"
              >
                <Iconify
                  icon="lucide:chevron-right"
                  className="!h-7 !w-7 !text-inherit"
                />
              </button>
            </>
          )}
        </div>

        {placeMedia?.[activeIndex]?.type === "post" && (
          <div
            className="absolute bottom-4 left-1/2 z-[99] flex w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 flex-col items-center justify-center rounded-xl bg-black/35 p-3 backdrop-blur"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex w-full flex-col gap-2 text-white">
              <div className="flex w-full items-start gap-3">
                <PostAvatar
                  src={placeMedia[activeIndex]?.post?.user?.profilePicture}
                  className="!h-10 !min-w-10 !w-10"
                />
                <div className="flex w-full flex-col items-start gap-0">
                  <span className="text-lg">
                    {placeMedia[activeIndex]?.post?.user?.username}
                  </span>
                  <span className="-mt-0.5 text-xs opacity-60">
                    {placeMedia[activeIndex]?.post?.createdAt?.split("T")?.[0]}
                  </span>
                </div>
              </div>
              <div className="flex w-full flex-col pl-2">
                <p className="text-base">
                  {placeMedia[activeIndex]?.post?.title}
                </p>
                {placeMedia[activeIndex]?.post?.description?.length > 0 && (
                  <p className="-mt-1 text-sm opacity-60">
                    {placeMedia[activeIndex]?.post?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacesImagesSlider;
